import type { IconComponent } from 'uikit';
import { cnConsta, FieldLabel, getByMap, IconClose, useForkRef, usePropsHandler } from 'uikit';

import type { ChangeEventHandler, FocusEventHandler, MouseEvent, ReactElement, Ref, RefAttributes } from 'react';
import { createElement, forwardRef, useCallback, useRef, useState } from 'react';
import type { PatternFormatProps } from 'react-number-format';
import { PatternFormat } from 'react-number-format';

import type { TextFieldProps } from './helpers';
import {
    sizeMap,
    textFieldPropFormDefault,
    textFieldPropSizeDefault,
    textFieldPropViewDefault,
    textFieldPropWidthDefault,
} from './helpers';

export const COMPONENT_NAME = 'TextField' as const;
export const cnTextField = cnConsta(COMPONENT_NAME);

type TFieldPatterFormat = Omit<PatternFormatProps, 'size' | 'onChange'> &
    TextFieldProps<string> &
    RefAttributes<HTMLElement> & { labelIcon?: IconComponent };

export const FieldPatterFormat = forwardRef(
    (props: TFieldPatterFormat, ref: Ref<HTMLDivElement>): ReactElement | null => {
        const textFieldRef = useRef<HTMLDivElement>(null);

        const {
            className,
            type = 'text',
            value,
            onChange,
            id,
            name,
            disabled,
            size = textFieldPropSizeDefault,
            view = textFieldPropViewDefault,
            form = textFieldPropFormDefault,
            state,
            status,
            width = textFieldPropWidthDefault,
            onBlur,
            onFocus,
            placeholder,
            leftSide,
            withClearButton,
            required,
            tabIndex,
            ariaLabel,
            label,
            inputContainerRef,
            labelPosition = 'top',
            iconSize: iconSizeProp,
            inputStyle,
            labelIcon,
            focused,
            format: patterFormat,
            mask: patternMask,
        } = usePropsHandler(COMPONENT_NAME, props, textFieldRef);
        const [focus, setFocus] = useState<boolean>(false);

        const LeftIcon = leftSide;
        const leftSideIsString = typeof leftSide === 'string';
        const iconSize = getByMap(sizeMap, size, iconSizeProp);

        const handleBlur = useCallback<FocusEventHandler<HTMLElement>>(
            (e) => {
                setFocus(false);
                onBlur?.(e);
            },
            [onBlur],
        );

        const handleFocus = useCallback<FocusEventHandler<HTMLElement>>(
            (e) => {
                setFocus(true);
                onFocus?.(e);
            },
            [onFocus],
        );

        const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
            (e) => {
                if (!disabled) {
                    onChange?.({ e, id, name, value: e.target.value ?? null });
                }
            },
            [disabled, id, name, onChange],
        );

        const commonProps = {
            className: cnTextField('Input'),
            value: value ?? '',
            onChange: handleChange,
            disabled,
            onBlur: handleBlur,
            onFocus: handleFocus,
            placeholder,
            required,
            tabIndex,
            name,
            id: id ? id.toString() : '',
            'aria-label': ariaLabel,
        };

        const handleClear = useCallback(
            (e: MouseEvent<HTMLButtonElement>) => {
                onChange?.({
                    e,
                    value: '',
                });
            },
            [onChange],
        );

        return (
            <div
                className={cnTextField({ labelPosition, size, view, width }, [className])}
                ref={useForkRef([ref, textFieldRef])}
            >
                {label && (
                    <FieldLabel required={required} className={cnTextField('Label', { labelPosition })} size={size}>
                        {label}
                        {labelIcon && createElement(labelIcon)}
                    </FieldLabel>
                )}

                <div className={cnTextField('Body')}>
                    <div
                        ref={inputContainerRef}
                        className={cnTextField('InputContainer', {
                            view,
                            form,
                            status: status || state,
                            disabled,
                            type,
                            focus: focus || focused,
                            withValue: !!value,
                        })}
                    >
                        {LeftIcon && (
                            <div
                                className={cnTextField('Side', {
                                    position: 'left',
                                    type: leftSideIsString ? 'string' : 'icon',
                                })}
                            >
                                {leftSideIsString ? (
                                    leftSide
                                ) : (
                                    <LeftIcon className={cnTextField('Icon')} size={iconSize} />
                                )}
                            </div>
                        )}

                        <PatternFormat
                            {...commonProps}
                            className="TextField-Input"
                            style={inputStyle}
                            format={patterFormat}
                            mask={patternMask}
                        />

                        {value && withClearButton && (
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={handleClear}
                                className={cnTextField('ClearButton')}
                            >
                                <IconClose size="xs" className={cnTextField('ClearButtonIcon')} />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    },
);

FieldPatterFormat.displayName = 'FieldPatterFormat';
